// src/pages/games/AllGames.tsx

import React from "react";

import {
  Link,
  useNavigate,
} from "react-router-dom";

import "./AllGames.css";


// =====================================================
// GAME TYPE
// =====================================================

interface Game {
  name: string;
  image: string;
  description: string;
  status: "available" | "coming-soon";
  guidePath: string;
}


// =====================================================
// GAMES
// =====================================================

const games: Game[] = [
  {
    name: "Fasoor",
    image: "/games/fasoor.png",
    description:
      "A strategic card game built around captures, combinations, Soors, and competitive scoring.",
    status: "available",
    guidePath: "/guides/fasoor",
  },

  {
    name: "Tika",
    image: "/games/tika.png",
    description:
      "A traditional card game joining the P-ARENA Game House.",
    status: "coming-soon",
    guidePath: "/guides/tika",
  },

  {
    name: "Bitruno",
    image: "/games/bitruno.png",
    description:
      "A competitive card game coming soon to P-ARENA.",
    status: "coming-soon",
    guidePath: "/guides/bitruno",
  },

  {
    name: "Charwali",
    image: "/games/charwali.png",
    description:
      "A social and competitive card game being prepared for P-ARENA.",
    status: "coming-soon",
    guidePath: "/guides/charwali",
  },

  {
    name: "Fiskoot",
    image: "/games/fiskoot.png",
    description:
      "Another classic card-game experience coming to the P-ARENA collection.",
    status: "coming-soon",
    guidePath: "/guides/fiskoot",
  },
];


// =====================================================
// ALL GAMES
// =====================================================

const AllGames: React.FC = () => {

  const navigate =
    useNavigate();


  // ===================================================
  // OPEN GAME
  // ===================================================

  const openGame = (
    game: Game
  ) => {

    if (
      game.status !== "available"
    ) {
      return;
    }

    /*
      Send the player through the existing
      Fasoor game flow.

      We do NOT create another game system here.
    */

    navigate(
      "/lobby"
    );
  };


  // ===================================================
  // UI
  // ===================================================

  return (
    <main className="all-games-page">

      {/* ============================================= */}
      {/* HERO                                          */}
      {/* ============================================= */}

      <section className="all-games-hero">

        <div className="all-games-hero-glow" />

        <div className="all-games-hero-content">

          {/* ========================================= */}
          {/* BREADCRUMB                                */}
          {/* ========================================= */}

          <nav
            className="all-games-breadcrumb"
            aria-label="Breadcrumb"
          >

            <Link to="/">
              P-ARENA
            </Link>

            <span>
              /
            </span>

            <strong>
              All Games
            </strong>

          </nav>


          {/* ========================================= */}
          {/* SUIT EMBLEM                               */}
          {/* ========================================= */}

          <div className="all-games-emblem">
            ♠
          </div>


          <p className="all-games-eyebrow">
            P-ARENA GAME HOUSE
          </p>


          <h1>
            All Games
          </h1>


          <p className="all-games-hero-description">
            Explore the growing collection of
            games inside P-ARENA. Play available
            titles today and discover what is
            coming next.
          </p>


          <div className="all-games-suits">
            <span>♠</span>
            <span>♥</span>
            <span>♦</span>
            <span>♣</span>
          </div>

        </div>

      </section>


      {/* ============================================= */}
      {/* MAIN CONTENT                                  */}
      {/* ============================================= */}

      <div className="all-games-content">

        {/* =========================================== */}
        {/* SECTION HEADER                              */}
        {/* =========================================== */}

        <section className="all-games-section">

          <div className="all-games-section-header">

            <div className="all-games-section-number">
              01
            </div>


            <div>

              <p className="all-games-small-label">
                THE COLLECTION
              </p>

              <h2>
                Choose Your Game
              </h2>

              <p className="all-games-section-description">
                Every game has its own rules,
                strategy, and experience.
              </p>

            </div>

          </div>


          {/* ========================================= */}
          {/* GAME GRID                                 */}
          {/* ========================================= */}

          <div className="all-games-grid">

            {games.map(
              (game) => {

                const available =
                  game.status ===
                  "available";


                return (

                  <article
                    key={game.name}
                    className={`
                      all-games-card
                      ${
                        available
                          ? "all-games-card-live"
                          : "all-games-card-coming"
                      }
                    `}
                  >

                    {/* ================================= */}
                    {/* IMAGE                             */}
                    {/* ================================= */}

                    <div className="all-games-image-wrapper">

                      <img
                        src={game.image}
                        alt={`${game.name} card game`}
                        className="all-games-image"
                      />


                      <div className="all-games-image-overlay" />


                      {/* =============================== */}
                      {/* STATUS                          */}
                      {/* =============================== */}

                      <div
                        className={`
                          all-games-status
                          ${
                            available
                              ? "all-games-status-live"
                              : ""
                          }
                        `}
                      >

                        {available
                          ? "AVAILABLE NOW"
                          : "COMING SOON"}

                      </div>


                      {/* =============================== */}
                      {/* NUMBER / SUIT                   */}
                      {/* =============================== */}

                      <div className="all-games-card-suit">
                        {game.name === "Fasoor"
                          ? "♣"
                          : game.name === "Tika"
                          ? "♦"
                          : game.name === "Bitruno"
                          ? "♠"
                          : game.name === "Charwali"
                          ? "♥"
                          : "♣"}
                      </div>

                    </div>


                    {/* ================================= */}
                    {/* DETAILS                           */}
                    {/* ================================= */}

                    <div className="all-games-card-body">

                      <h3>
                        {game.name}
                      </h3>


                      <p>
                        {game.description}
                      </p>


                      {/* =============================== */}
                      {/* ACTIONS                         */}
                      {/* =============================== */}

                      <div className="all-games-actions">

                        {available ? (

                          <button
                            type="button"
                            className="all-games-play-button"
                            onClick={() =>
                              openGame(
                                game
                              )
                            }
                          >
                            <span>
                              ▶
                            </span>

                            Play Now
                          </button>

                        ) : (

                          <button
                            type="button"
                            className="all-games-disabled-button"
                            disabled
                          >
                            Coming Soon
                          </button>

                        )}


                        <Link
                          to={game.guidePath}
                          className="all-games-guide-link"
                        >
                          Game Guide

                          <span>
                            →
                          </span>
                        </Link>

                      </div>

                    </div>

                  </article>

                );
              }
            )}

          </div>

        </section>


        {/* =========================================== */}
        {/* AVAILABLE NOW                               */}
        {/* =========================================== */}

        <section className="all-games-feature">

          <div className="all-games-feature-content">

            <p className="all-games-small-label">
              AVAILABLE NOW
            </p>

            <h2>
              Enter the World
              of Fasoor
            </h2>


            <p>
              Capture cards, build combinations,
              score Soors, compete for points,
              and become the first player to
              reach the winning score.
            </p>


            <div className="all-games-feature-actions">

              <button
                type="button"
                className="all-games-primary-button"
                onClick={() =>
                  navigate(
                    "/lobby"
                  )
                }
              >
                ▶ Play Fasoor
              </button>


              <Link
                to="/guides/fasoor"
                className="all-games-secondary-button"
              >
                Read Game Guide
              </Link>

            </div>

          </div>


          <div className="all-games-feature-symbol">

            <span>
              F
            </span>

            <div>
              ♠ ♥ ♦ ♣
            </div>

          </div>

        </section>


        {/* =========================================== */}
        {/* FUTURE GAMES                                */}
        {/* =========================================== */}

        <section className="all-games-coming-section">

          <div className="all-games-coming-line" />


          <div className="all-games-coming-content">

            <div className="all-games-coming-suits">
              ♠ &nbsp; ♥ &nbsp; ♦ &nbsp; ♣
            </div>


            <p className="all-games-small-label">
              THE GAME HOUSE IS GROWING
            </p>


            <h2>
              More Games Are Coming
            </h2>


            <p>
              Fasoor is only the beginning.
              Additional games will become
              available as the P-ARENA
              Game House continues to grow.
            </p>


            <Link
              to="/"
              className="all-games-home-link"
            >
              Return to P-ARENA
              <span>→</span>
            </Link>

          </div>


          <div className="all-games-coming-line" />

        </section>

      </div>

    </main>
  );
};


export default AllGames;